import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import EditIcon from "src/core/assets/images/icons/edit.svg?react";
import HalfPersonIcon from "src/core/assets/images/icons/halfPerson.svg?react";
import MailIcon from "src/core/assets/images/icons/mail.svg?react";
import PhoneIcon from "src/core/assets/images/icons/phone.svg?react";
import { FollowButtonField } from "src/core/components/create-client/FollowButtonField";
import { Button } from "src/core/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/core/components/ui/tooltip";
import { useFolder } from "src/core/contexts/FolderContext";
import { useFollowFolderMutation, useUnfollowFolderMutation } from "src/core/gql/graphql";
import { cn } from "src/core/lib/utils";
import { Contact, Owner } from "src/core/types/contact";

type Props = {
  contactPerson?: Contact | null;
  owner?: Owner | null;
  isOwner: boolean;
  onEdit: () => void;
  canEdit: boolean;
  canFollow: boolean;
};

export const ContactOwnerBlock = ({
  contactPerson,
  owner,
  isOwner,
  onEdit,
  canEdit,
  canFollow,
}: Props) => {
  const { t } = useTranslation();
  const { refetch, folderFile } = useFolder();
  const [unfollowFolder] = useUnfollowFolderMutation();
  const [followFolder] = useFollowFolderMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(
    folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser ?? false
  );
  const [nbFollowers, setNbFollowers] = useState(() => {
    const totalFollowers =
      folderFile.folderFile?.currentCustomerFolder?.followingUsers?.length ?? 0;
    const isCurrentUserFollowing =
      folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser ?? false;
    return isCurrentUserFollowing ? Math.max(0, totalFollowers - 1) : totalFollowers;
  });

  useEffect(() => {
    const isCurrentUserFollowing =
      folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser ?? false;
    const totalFollowers =
      folderFile.folderFile?.currentCustomerFolder?.followingUsers?.length ?? 0;

    setIsFollowing(isCurrentUserFollowing);
    setNbFollowers(isCurrentUserFollowing ? Math.max(0, totalFollowers - 1) : totalFollowers);
  }, [
    folderFile.folderFile?.currentCustomerFolder?.followedByCurrentUser,
    folderFile.folderFile?.currentCustomerFolder?.followingUsers?.length,
  ]);
  const handleFollow = async () => {
    setIsLoading(true);
    const customerFolderId = folderFile.folderFile?.currentCustomerFolder?.id;
    if (!customerFolderId) {
      setIsLoading(false);
      return;
    }
    try {
      if (isFollowing) {
        await unfollowFolder({
          variables: { customerFolderId },
        });
      } else {
        await followFolder({
          variables: { customerFolderId },
        });
      }
      await refetch();
    } catch (error) {
      console.error("Error following/unfollowing folder:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-row">
      <div className="flex flex-row items-center">
        {canFollow && (
          <FollowButtonField isFollowing={isFollowing} onClick={handleFollow} loading={isLoading} />
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn("flex flex-col  ml-5", {
                  "text-muted-foreground": nbFollowers === 0 && !isFollowing,
                })}
              >
                <div className="flex flex-row items-center font-light">
                  {canFollow && isFollowing && (
                    <span className="font-medium mr-1">{t("folder.header.meAnd")}</span>
                  )}
                  {!canFollow && <span className="mr-1">{t("folder.header.followed")}</span>}
                  {nbFollowers} <HalfPersonIcon className="ml-1" />
                </div>
                <div className="font-light">
                  {t("folder.header.collaborator", { count: nbFollowers })}
                </div>
              </div>
            </TooltipTrigger>
            {nbFollowers > 0 && (
              <TooltipContent side="bottom" align="center">
                <div className="flex flex-col gap-1">
                  {folderFile.folderFile?.currentCustomerFolder?.followingUsers?.map((follower) => (
                    <div key={follower.id}>
                      {follower.firstName} {follower.lastName}
                    </div>
                  ))}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="border-r border-border mx-10 h-30" />
      <div className="group flex flex-row">
        {contactPerson?.email && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a data-testid="mail-link" href={`mailto:${contactPerson.email}`}>
                  <MailIcon className="cursor-pointer hover:text-foreground text-muted-foreground mr-5" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="center">
                <div className="flex flex-col gap-1">{contactPerson.email}</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <div className="flex flex-col w-[110px]">
          {contactPerson &&
          (contactPerson.firstName || contactPerson.lastName || contactPerson.phone) ? (
            <div className="ml-5">
              {(contactPerson.firstName || contactPerson.lastName) && (
                <div className="font-medium text-[13px] leading-[16px] max-w-[110px] truncate">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span data-testid="contact-name" className="">
                          {[contactPerson.firstName, contactPerson.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="center">
                        <div className="flex flex-col gap-1">
                          {[contactPerson.firstName, contactPerson.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              {contactPerson.phone && (
                <div className="flex flex-row text-[11px] leading-[14px] items-center max-w-[100px] truncate">
                  <PhoneIcon className="mr-[2px]" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="" data-testid="contact-phone">
                          {contactPerson.phone}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="center">
                        <div className="flex flex-col gap-1">{contactPerson.phone}</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-5 font-light">
              <div className="font-medium text-[13px] leading-[16px]">
                {t("folder.info.contactTitle")}
              </div>
              <span className="font-light text-muted-foreground">{t("folder.info.noContact")}</span>
            </div>
          )}
        </div>

        <div className="border-r border-border mx-10 h-30" />

        <div className="flex flex-col">
          <div className="font-medium text-[13px] leading-[16px]">
            {t("folder.info.expertComptableTitle")}
          </div>
          {isOwner ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span data-testid="owner-name" className="folder-owner-name">
                    {[owner?.firstName, owner?.lastName].filter(Boolean).join(" ")}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="center">
                  <div className="flex flex-col gap-1">
                    {[owner?.firstName, owner?.lastName].filter(Boolean).join(" ")}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="font-light text-muted-foreground">
              {t("folder.info.noExpertComptable")}
            </span>
          )}
        </div>
        {canEdit && (
          <div
            className={cn(
              "text-primary ml-10",
              "opacity-0 invisible pointer-events-none",
              "transition-opacity duration-150",
              "group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto",
              "group-focus-within:opacity-100 group-focus-within:visible group-focus-within:pointer-events-auto"
            )}
          >
            <Button
              variant="icon"
              size="icon"
              className="cursor-pointer"
              onClick={onEdit}
              data-testid="edit-contact-button"
            >
              <EditIcon />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
